/**
 * Pure drift-detection + GitHub link logic for the deployed version indicator.
 *
 * Drift compares the frontend build sha (`BUILD_INFO.gitSha`) against the sha
 * the backend reports at `GET /api/version`:
 *   - both known & equal  → 'in-sync' (green)
 *   - both known & differ → 'drift'   (amber)
 *   - either unknown/sentinel/unreachable → 'unknown' (grey)
 *
 * All functions here are side-effect free so the traffic-light + URL contract
 * can be unit tested in isolation.
 */

/** Traffic-light state of the frontend vs backend build comparison. */
export type VersionDriftStatus = 'in-sync' | 'drift' | 'unknown'

/** Kind + href of the GitHub link surfaced in the version popover. */
export interface VersionGithubLink {
  readonly kind: 'commit' | 'compare'
  readonly url: string
}

/** Private repo base — the commit/compare links only resolve for GitHub members. */
const GITHUB_REPO_BASE_URL = 'https://github.com/ludwigvonmises3/blockandtaxes'

/**
 * Sentinel sha values that mean "no real git metadata is available" and must
 * never be treated as a comparable commit (dev server, no-git build, or an
 * `'unknown'` backend field). Compared case-insensitively.
 */
const UNKNOWN_SHA_SENTINELS: ReadonlySet<string> = new Set(['dev', 'unknown', 'local', ''])

/** True when `sha` is a real commit hash (not a sentinel / missing value). */
export function isKnownSha(sha: string | undefined | null): boolean {
  if (sha === undefined || sha === null) return false
  return !UNKNOWN_SHA_SENTINELS.has(sha.toLowerCase())
}

/** Resolve the traffic-light drift status from the two build shas. */
export function resolveVersionDriftStatus(
  frontendSha: string,
  backendSha: string | undefined,
): VersionDriftStatus {
  if (!isKnownSha(frontendSha) || !isKnownSha(backendSha)) {
    return 'unknown'
  }
  return frontendSha === backendSha ? 'in-sync' : 'drift'
}

/** `https://github.com/<owner>/<repo>/commit/<fullSha>`. */
export function buildCommitUrl(fullSha: string): string {
  return `${GITHUB_REPO_BASE_URL}/commit/${fullSha}`
}

/** `https://github.com/<owner>/<repo>/compare/<base>...<head>`. */
export function buildCompareUrl(baseFullSha: string, headFullSha: string): string {
  return `${GITHUB_REPO_BASE_URL}/compare/${baseFullSha}...${headFullSha}`
}

/**
 * Resolve the single GitHub link the popover should offer:
 *   - drift (both shas known) → compare backend...frontend
 *   - otherwise → commit link to the frontend sha (the deployed FE), or the
 *     backend sha when only that one is known
 *   - neither known → no link (`null`)
 */
export function resolveVersionGithubLink(
  driftStatus: VersionDriftStatus,
  frontendSha: string,
  backendSha: string | undefined,
): VersionGithubLink | null {
  if (driftStatus === 'drift' && isKnownSha(frontendSha) && isKnownSha(backendSha)) {
    // `backendSha` is provably a string here (isKnownSha rejects undefined).
    return { kind: 'compare', url: buildCompareUrl(backendSha as string, frontendSha) }
  }
  if (isKnownSha(frontendSha)) {
    return { kind: 'commit', url: buildCommitUrl(frontendSha) }
  }
  if (isKnownSha(backendSha)) {
    return { kind: 'commit', url: buildCommitUrl(backendSha as string) }
  }
  return null
}
